import { PropsWithChildren, ReactNode, useCallback } from 'react';

import { useSetAnalyticsGlobal } from '@cast/analytics';
import {
  DrawerProvider,
  Skeleton,
  Tab,
  TabContent,
  Tabs,
  TabsProvider,
  Stack,
  tokens,
} from '@cast/design-system';
import { DashboardTab } from '@cast/features-cluster-dashboard';
import {
  clustersSearchCriteria,
  useClustersSearchData,
  useSearchContextClusters,
} from '@cast/features-cluster-filters';
import { ClustersListCard } from '@cast/features-cluster-list';
import type { RenderClusterRowActions } from '@cast/features-cluster-list';
import { SearchProvider } from '@cast/shared-search-ui';
import { FailedToLoad } from '@cast/shared-messages-ui';
import { Cluster } from '@cast/types';

import { useRouterSearchParams } from '@cast/infrastructure-navigation';

import { ClustersEmptyState } from './components/ClustersEmptyState';
import { ClustersPageHeader } from './components/ClustersPageHeader';
import { ConnectClusterButton } from './components/ConnectClusterButton';

type ClustersTab = 'dashboard' | 'list';

const isClustersTab = (value: string | null): value is ClustersTab =>
  value === 'dashboard' || value === 'list';

type ClustersDashboardProps = {
  onConnectCluster?: () => void;
  isConnectClusterDisabled?: boolean;
  clusterPathTemplate?: string;
  renderRowActions?: RenderClusterRowActions;
  banners?: ReactNode;
  /** Shown under Connect in the empty state (e.g. reporting trial alert). */
  emptyStateBanner?: ReactNode;
};

type ClustersDashboardContentWrapperProps = {
  testId?: string;
  banners?: ReactNode;
};

const ClustersDashboardContentWrapper = ({
  testId,
  banners,
  children,
}: PropsWithChildren<ClustersDashboardContentWrapperProps>) => {
  return (
    <Stack gap={16} data-testid={testId}>
      {banners}
      {children}
    </Stack>
  );
};

const ClustersDashboardContent = ({
  onConnectCluster,
  isConnectClusterDisabled,
  clusterPathTemplate,
  renderRowActions,
  banners,
  emptyStateBanner,
}: ClustersDashboardProps) => {
  const [searchParams, setSearchParams] = useRouterSearchParams();
  const { allClusters, isLoading, error, refetch } =
    useSearchContextClusters<Cluster>();

  const viewParam = searchParams.get('view');
  const currentTab: ClustersTab = isClustersTab(viewParam)
    ? viewParam
    : 'dashboard';

  const setCurrentTab = useCallback(
    (tab: ClustersTab) => {
      void setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'dashboard') {
          next.delete('view');
        } else {
          next.set('view', tab);
        }
        return next;
      });
    },
    [setSearchParams]
  );

  if (isLoading) {
    return (
      <ClustersDashboardContentWrapper
        testId="clusters-loading"
        banners={banners}
      >
        <ClustersPageHeader />
        <Skeleton variant="rounded" height={420} />
      </ClustersDashboardContentWrapper>
    );
  }

  if (error) {
    return (
      <ClustersDashboardContentWrapper banners={banners}>
        <ClustersPageHeader />
        <FailedToLoad title="Failed to load clusters" refresh={refetch} />
      </ClustersDashboardContentWrapper>
    );
  }

  if (allClusters.length === 0) {
    return (
      <ClustersDashboardContentWrapper banners={banners}>
        <ClustersPageHeader />
        <ClustersEmptyState
          onConnectCluster={onConnectCluster}
          isConnectClusterDisabled={isConnectClusterDisabled}
          banner={emptyStateBanner}
        />
      </ClustersDashboardContentWrapper>
    );
  }

  return (
    <TabsProvider<ClustersTab>
      currentTab={currentTab}
      setCurrentTab={setCurrentTab}
    >
      <ClustersDashboardContentWrapper banners={banners}>
        <ClustersPageHeader
          showFilters
          onConnectCluster={onConnectCluster}
          isConnectClusterDisabled={isConnectClusterDisabled}
        />
        <Tabs<ClustersTab>
          sx={{
            borderBottom: tokens.theme.border.structural.neutral,
            width: '100%',
          }}
        >
          <Tab value="dashboard" label="Dashboard" />
          <Tab value="list" label="Cluster list" />
        </Tabs>
        <TabContent value="dashboard">
          <DashboardTab
            emptyStateActions={
              <ConnectClusterButton
                onConnectCluster={onConnectCluster}
                disabled={isConnectClusterDisabled}
              />
            }
            clusterPathTemplate={clusterPathTemplate}
          />
        </TabContent>
        <TabContent value="list" renderInactive>
          <ClustersListCard
            emptyStateActions={
              <ConnectClusterButton
                onConnectCluster={onConnectCluster}
                disabled={isConnectClusterDisabled}
              />
            }
            clusterPathTemplate={clusterPathTemplate}
            renderRowActions={renderRowActions}
          />
        </TabContent>
      </ClustersDashboardContentWrapper>
    </TabsProvider>
  );
};

export const ClustersDashboard = ({
  onConnectCluster,
  isConnectClusterDisabled,
  clusterPathTemplate,
  renderRowActions,
  banners,
  emptyStateBanner,
}: ClustersDashboardProps = {}) => {
  useSetAnalyticsGlobal('feature', 'Clusters');

  return (
    <SearchProvider<Cluster>
      criteria={clustersSearchCriteria}
      useData={useClustersSearchData}
      urlKey="cluster-list"
    >
      <DrawerProvider>
        <ClustersDashboardContent
          onConnectCluster={onConnectCluster}
          isConnectClusterDisabled={isConnectClusterDisabled}
          clusterPathTemplate={clusterPathTemplate}
          renderRowActions={renderRowActions}
          banners={banners}
          emptyStateBanner={emptyStateBanner}
        />
      </DrawerProvider>
    </SearchProvider>
  );
};
